import { toast } from "react-toastify";

class VideoRecorder {
    constructor() {
        if (VideoRecorder.instance) {
            return VideoRecorder.instance;
        }

        this.stream = null;
        this.totalSeconds = -1;
        this.recordedChunks = [];
        this.mediaRecorder = null;
        this.onRecordingStop = null;
        this.stopTimeout = null;
        this.recordingStartTime = null;

        VideoRecorder.instance = this;
    }

    /**
     * Initialize with stream and callback. MediaRecorder is created once
     * and reused across recordings until stream changes or clearVideoRecorder().
     */
    init(stream, onRecordingStop, totalSeconds = -1) {
        if (!stream || !onRecordingStop) {
            toast.error("Stream and onRecordingStop callback are required");
            return;
        }

        this.onRecordingStop = onRecordingStop;
        this.totalSeconds = totalSeconds;
        this.recordedChunks = [];

        // Only (re)create MediaRecorder if stream changed or recorder missing
        if (this.stream !== stream || !this.mediaRecorder) {
            this.stream = stream;
            this._createMediaRecorder();
        }
    }

    _createMediaRecorder() {
        if (!this.stream) return;

        // Clean up previous recorder if any
        if (this.mediaRecorder) {
            this.mediaRecorder.removeEventListener("dataavailable", this.dataAvailableHandler);
            if (this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
        }

        this.mediaRecorder = new MediaRecorder(this.stream, {
            videoMaximizeFrameRate: true
        });

        this.mediaRecorder.addEventListener(
            "dataavailable",
            this.dataAvailableHandler
        );

        this.mediaRecorder.onstop = async () => {
            if (this.stopTimeout) {
                clearTimeout(this.stopTimeout);
                this.stopTimeout = null;
            }
            const chunks = this.recordedChunks;
            const callback = this.onRecordingStop;
            this.recordedChunks = [];
            this.recordingStartTime = null;
            this.totalSeconds = -1;
            // MediaRecorder stays alive (state='inactive') — ready for next recording
            await callback(chunks);
        };

        this.mediaRecorder.onstart = () => {
            this.recordingStartTime = Date.now();
            if (this.totalSeconds > 0 && this.totalSeconds !== Infinity) {
                const duration = this.totalSeconds * 1000;
                this.stopTimeout = setTimeout(() => {
                    if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
                        this.stopRecording();
                    }
                }, duration);
            }
        };
    }

    dataAvailableHandler = ({ data }) => {
        if (data.size > 0) {
            this.recordedChunks.push(data);
        }
    };

    startRecording = () => {
        if (!this.stream) {
            throw new Error("Stream is not initialized");
        }

        this.recordedChunks = [];

        if (this.mediaRecorder && this.mediaRecorder.state === 'inactive') {
            // timeslice=100 ensures dataavailable fires every 100ms for reliable capture
            this.mediaRecorder.start(100);
            return;
        }

        // Fallback: recorder lost, recreate
        this._createMediaRecorder();
        if (this.mediaRecorder) {
            this.mediaRecorder.start(100);
        }
    };

    stopRecording = () => {
        if (this.stopTimeout) {
            clearTimeout(this.stopTimeout);
            this.stopTimeout = null;
        }
        if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
            this.mediaRecorder.stop();
        }
    };

    getChunks = () => {
        return this.recordedChunks;
    };

    clearVideoRecorder = () => {
        if (this.stopTimeout) {
            clearTimeout(this.stopTimeout);
            this.stopTimeout = null;
        }
        if (this.mediaRecorder) {
            this.mediaRecorder.removeEventListener("dataavailable", this.dataAvailableHandler);
            if (this.mediaRecorder.state !== 'inactive') {
                this.mediaRecorder.stop();
            }
            this.mediaRecorder = null;
        }
        this.stream = null;
        this.totalSeconds = -1;
        this.recordedChunks = [];
        this.onRecordingStop = null;
        this.recordingStartTime = null;
    };
}

const instance = new VideoRecorder();

export default instance;
